import type { AxiosResponse } from "axios";
import type {
  CheckAqsatiCustomerEligibilityInputDto,
  SignUpOutputDto,
  UpdateDetailsBodyInputDto,
} from "../types/codegen";
import { type PlatformApi, type SetNavigationBarParams } from "./types";
import api, { unauthenticatedApi } from "../api/api";
import {
  type PaymentProviderType,
  type BnplPaymentProvider,
  type StandardPaymentProvider,
  type PaymentProvider,
} from "../payments/types";
import { isProduction } from "../constants";
import { bnplPaymentProvider } from "../payments/bnplPaymentProvider";
import { superQiPaymentProvider } from "../payments/superQiPaymentProvider";
import { SignInError } from "../errors/SignInError";
import { PersonalDataConsentError } from "../errors/PersonalDataConsentError";
import { CreditCardsConsentError } from "../errors/CreditCardsConsentError";
import { promiseWithTimeout } from "../utils/promiseWithTimeout";

class SuperQiApi implements PlatformApi {
  getLocale() {
    return promiseWithTimeout<string>(
      (resolve, reject) => {
        my.getSystemInfo({
          success: (res) => {
            const fetchedLocale = res.language.split("-")[0];
            resolve(fetchedLocale);
          },
          fail: (error) => {
            reject(new Error("Failed to get system info", { cause: error }));
          },
        });
      },
      {
        timeoutMs: 300,
        timeoutError: new Error("Failed to get system info", {
          cause: "timeout",
        }),
      },
    );
  }

  setNavigationBar({ backgroundColor, image }: SetNavigationBarParams) {
    my?.setNavigationBar({
      backgroundColor,
      // this is a workaround to hide the logo, as there is no api explicitely doing that
      // title and image are mutually exclusive, so setting title (must be non-empty string) hides the logo
      ...(image ? { image } : { title: " " }),
    });
  }

  async signIn() {
    const authCode = await new Promise<string>((resolve, reject) => {
      my?.getAuthCode({
        scopes: ["auth_base"], // silent log in without consents
        success: (res) => {
          resolve(res.authCode);
        },
        fail: (error) => {
          reject(
            new SignInError(
              `Failed to get auth code. Message: ${error.errorMessage}. Error code: ${error.error}`,
              error.error === 11
                ? "cancelled"
                : error.error === 10
                  ? "network_fail"
                  : "unknown",
            ),
          );
        },
      });
    });

    const { data } = await unauthenticatedApi.post<SignUpOutputDto>(
      "/auth/super-qi/sign-up",
      {
        authCode,
        miniProgramClient: "TRAVEL_ZONE",
      },
    );

    return data.data;
  }

  async updatePersonalData() {
    const authCode = await new Promise<string>((resolve, reject) => {
      my?.getAuthCode({
        scopes: ["auth_base", "USER_LOGIN_ID", "USER_NAME"],
        success: (res) => {
          resolve(res.authCode);
        },
        fail: (error) => {
          reject(
            new PersonalDataConsentError(
              `Failed to get auth code for personal data. Message: ${error.errorMessage}. Error code: ${error.error}`,
              error.error === 11
                ? "cancelled"
                : error.error === 10
                  ? "network_fail"
                  : "unknown",
            ),
          );
        },
      });
    });

    return void api.post<void, AxiosResponse<void>, UpdateDetailsBodyInputDto>(
      "/auth/super-qi/update-details",
      {
        authCode,
        miniProgramClient: "TRAVEL_ZONE",
      },
    );
  }

  getPaymentProvider(providerType: "bnpl"): BnplPaymentProvider;
  getPaymentProvider(providerType: "standard"): StandardPaymentProvider;
  getPaymentProvider(providerType: PaymentProviderType): PaymentProvider {
    switch (providerType) {
      case "bnpl":
        return bnplPaymentProvider;
      case "standard":
        return superQiPaymentProvider;
      default:
        throw new Error("Unsupported payment provider type");
    }
  }

  async getEligibilityPayload(cardNumber?: string) {
    if (cardNumber) {
      // If card number is provided, use it directly
      return { cardNumber };
    }

    return new Promise<CheckAqsatiCustomerEligibilityInputDto>(
      (resolve, reject) => {
        // https://miniprogram.alipay.com/docs/miniprogram/mpdev/api_openapi_getauthcode
        my?.getAuthCode({
          scopes: ["auth_base", "CARD_LIST"],
          success: (res) => {
            if (!isProduction) {
              resolve({ cardNumber: "6330153657143355" });
              return;
            }

            resolve({ miniProgramAccessToken: res.authCode });
          },
          fail: (error) => {
            reject(
              new CreditCardsConsentError(
                `Failed to get auth code for credit card data. Message: ${error.errorMessage}. Error code: ${error.error}`,
                error.error === 11
                  ? "cancelled"
                  : error.error === 10
                    ? "network_fail"
                    : "unknown",
              ),
            );
          },
        });
      },
    );
  }
}

export const superQiApi: PlatformApi = new SuperQiApi();
